// ============================================================
// useSecureInvitation — drives the recipient portal's full journey:
//
//   restore (verify-session → manifest)      — a refresh lands on server truth
//   └─ else resolve (non-consuming)          — honest invitation summary
//        → redeem (signed-link attempt)      — explicit recipient action
//            → code_required (fallback code) — hidden until needed/chosen
//        → verify-session → manifest         — sealed httpOnly session
//            → nda / preferences (persisted) — real onboarding, server truth
//            → portal_home                   — the real project workspace
//
// The browser never holds a token, code hash, or session secret; every step
// is a server round-trip and every refresh restores from the manifest. All
// denials are generic (no enumeration).
// ============================================================

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { api, type RecipientManifest } from '../../../api/client'
import {
  resolveSecureInvitation, viewFromMemberState, viewFromManifest, RECIPIENT_COPY,
  type RecipientView, type SecureInvitationDisplay,
} from './secureInvitation'

const EMPTY_DISPLAY: SecureInvitationDisplay = {
  projectName: null, inviterName: null, purpose: null, expiresAt: null,
  ndaRequired: false, notificationPreferencesRequired: false,
  accountMode: null, fallbackAvailable: false, correlationId: null,
}

export interface ModuleUpdate {
  id: string
  body: string
  published_at: string
}

export interface SecureInvitationState {
  view: RecipientView
  display: SecureInvitationDisplay
  manifest: RecipientManifest | null
  updates: ModuleUpdate[] | null
  message: string
  busy: boolean
  hasReference: boolean
  showFallback: boolean
  fallbackCode: string
  fallbackNotice: string | null
  emailUpdates: boolean
  setEmailUpdates: (v: boolean) => void
  retry: () => void
  continueOnboarding: () => void
  revealFallback: () => void
  hideFallback: () => void
  setFallbackCode: (v: string) => void
  submitFallback: () => void
}

export function useSecureInvitation(): SecureInvitationState {
  const params = useParams<{ token?: string }>()
  const [search] = useSearchParams()

  // Signed link reference: route token preferred, else ?invite=/?token= query.
  const ref = useMemo(() => {
    const token = (params.token ?? search.get('token') ?? '').trim()
    const invite_id = (search.get('invite') ?? search.get('invite_id') ?? '').trim()
    return { token: token || undefined, invite_id: invite_id || undefined }
  }, [params.token, search])

  const hasReference = Boolean(ref.token || ref.invite_id)

  const [view, setView] = useState<RecipientView>('preparing')
  const [display, setDisplay] = useState<SecureInvitationDisplay>(EMPTY_DISPLAY)
  const [manifest, setManifest] = useState<RecipientManifest | null>(null)
  const [updates, setUpdates] = useState<ModuleUpdate[] | null>(null)
  const [message, setMessage] = useState<string>(RECIPIENT_COPY.preparing.body)
  const [busy, setBusy] = useState<boolean>(false)
  const [showFallback, setShowFallback] = useState<boolean>(false)
  const [fallbackCode, setFallbackCode] = useState<string>('')
  const [fallbackNotice, setFallbackNotice] = useState<string | null>(null)
  const [emailUpdates, setEmailUpdates] = useState<boolean>(false)
  const [nonce, setNonce] = useState<number>(0) // retry trigger

  // Enter the verified portal from MANIFEST server truth: the right
  // onboarding step, or the workspace itself (+ its published Updates).
  const enterFromManifest = useCallback(async (m: RecipientManifest) => {
    setManifest(m)
    setEmailUpdates(m.onboarding.preferences.email_updates)
    const next = viewFromManifest(m)
    if (next === 'portal_home' && m.modules.some((mod) => mod.key === 'updates' && mod.state === 'live')) {
      try {
        const r = await api.portalSession.module('updates')
        setUpdates(r.available && r.content?.updates ? r.content.updates : [])
      } catch {
        setUpdates(null) // content fetch failure is not a session failure
      }
    }
    setMessage(RECIPIENT_COPY[next].body)
    setView(next)
  }, [])

  // Verified session → manifest → onboarding/portal. Returns false when no
  // usable session exists (caller falls back to the resolve flow).
  const tryRestoreSession = useCallback(async (): Promise<boolean> => {
    try {
      const v = await api.access.verifySession({ invite_id: ref.invite_id ?? ref.token })
      if (v.ok !== true || v.state !== 'verified') {
        // Terminal session states surface honestly; everything else falls
        // back to resolve (which produces its own honest state).
        if (v.state === 'revoked' || v.state === 'expired') {
          setView(v.state)
          setMessage(v.message || RECIPIENT_COPY[v.state].body)
          return true
        }
        return false
      }
      const m = await api.portalSession.manifest()
      if (m.ok !== true || !m.manifest) {
        if (m.state === 'revoked' || m.state === 'expired') {
          setView(m.state)
          setMessage(m.message || RECIPIENT_COPY[m.state].body)
          return true
        }
        return false
      }
      await enterFromManifest(m.manifest)
      return true
    } catch {
      return false
    }
  }, [ref, enterFromManifest])

  const run = useCallback(async () => {
    if (!hasReference) {
      setView('unverifiable')
      setMessage(RECIPIENT_COPY.unverifiable.body)
      return
    }
    setBusy(true)
    setView('preparing')
    setMessage(RECIPIENT_COPY.preparing.body)

    // A refresh or return visit restores the verified portal from server truth.
    if (await tryRestoreSession()) {
      setBusy(false)
      return
    }

    // No session — the non-consuming resolve shows the honest invitation state.
    const outcome = await resolveSecureInvitation(ref)
    setDisplay(outcome.display)
    setMessage(outcome.message || RECIPIENT_COPY[outcome.view].body)
    setView(outcome.view)
    setBusy(false)
  }, [hasReference, ref, tryRestoreSession])

  useEffect(() => { void run() }, [run, nonce])

  const retry = useCallback(() => {
    setFallbackNotice(null)
    setNonce((n) => n + 1)
  }, [])

  // Redeem (signed-link when code is absent; fallback code when present),
  // then verify + enter. Every denial maps to a generic honest view.
  const redeem = useCallback(async (code?: string) => {
    setBusy(true)
    setView('redeeming')
    setMessage(RECIPIENT_COPY.redeeming.body)
    setFallbackNotice(null)
    try {
      const r = await api.access.redeem({
        invite_id: ref.invite_id ?? ref.token,
        token: ref.token,
        code: code || undefined,
      })
      if (r.ok === true && r.redeemed === true) {
        if (!(await tryRestoreSession())) {
          // Redeemed but the session didn't verify — honest transient state.
          setView('link_failed')
          setMessage(RECIPIENT_COPY.link_failed.body)
        }
        setBusy(false)
        return
      }
      const next = viewFromMemberState(r.state)
      if (next === 'code_required') {
        setView('code_required')
        // A failed CODE attempt shows the denial inside the code form; a
        // codeless attempt just opens the form with its own copy.
        setFallbackNotice(code ? r.message || RECIPIENT_COPY.denied.body : null)
        setMessage(RECIPIENT_COPY.code_required.body)
      } else if (next === 'ready') {
        setView('ready')
        setMessage(RECIPIENT_COPY.ready.body)
      } else {
        setView(next)
        setMessage(r.message || RECIPIENT_COPY[next].body)
      }
    } catch {
      setView('link_failed')
      setMessage(RECIPIENT_COPY.link_failed.body)
    }
    setBusy(false)
  }, [ref, tryRestoreSession])

  // The single Continue action — meaning depends on where the recipient is.
  const continueOnboarding = useCallback(() => {
    if (busy) return
    if (view === 'ready' || view === 'expires_soon') {
      void redeem() // explicit recipient action → signed-link redemption
      return
    }
    if (view === 'nda') {
      setBusy(true)
      void api.portalSession
        .onboarding({ step: 'nda', accept: true })
        .then(async (r) => {
          if (r.ok === true && r.manifest) await enterFromManifest(r.manifest)
          else {
            setView('link_failed')
            setMessage(r.message || RECIPIENT_COPY.link_failed.body)
          }
        })
        .catch(() => {
          setView('link_failed')
          setMessage(RECIPIENT_COPY.link_failed.body)
        })
        .finally(() => setBusy(false))
      return
    }
    if (view === 'notification_preferences') {
      setBusy(true)
      void api.portalSession
        .onboarding({ step: 'preferences', portal_notifications: true, email_updates: emailUpdates })
        .then(async (r) => {
          if (r.ok === true && r.manifest) await enterFromManifest(r.manifest)
          else {
            setView('link_failed')
            setMessage(r.message || RECIPIENT_COPY.link_failed.body)
          }
        })
        .catch(() => {
          setView('link_failed')
          setMessage(RECIPIENT_COPY.link_failed.body)
        })
        .finally(() => setBusy(false))
    }
  }, [busy, view, redeem, emailUpdates, enterFromManifest])

  const revealFallback = useCallback(() => setShowFallback(true), [])
  const hideFallback = useCallback(() => { setShowFallback(false); setFallbackNotice(null) }, [])

  // REAL fallback redemption: the one-time code is sent in the request body
  // (never a URL) and, on success, the same verified-session path opens.
  const submitFallback = useCallback(() => {
    const code = fallbackCode.trim()
    if (!code) {
      setFallbackNotice('Enter the access code you were given.')
      return
    }
    void redeem(code)
  }, [fallbackCode, redeem])

  return {
    view, display, manifest, updates, message, busy, hasReference,
    showFallback, fallbackCode, fallbackNotice,
    emailUpdates, setEmailUpdates,
    retry, continueOnboarding, revealFallback, hideFallback, setFallbackCode, submitFallback,
  }
}